import { observable, action, computed } from 'mobx'
import { Api, getMainImageSrcForObj } from 'utilities'
import { computedFn } from 'mobx-utils'
import { colors } from 'assets'
import {
  DraggingInfo
} from './types'
import {
  RootStore, EDITOR_STORE, EDITOR_TIMELINE_STORE, GENERAL_STORE,
  EDITOR_INTERACTIVE_AREAS_STORE
} from 'store'
import { getEntityClass } from './utils'
import { Rect, VideoEntityEvent, EntityClassTypes, InteractiveAreaAPI, Shot, VideoEventTypes, IaAction, IaActionTypes } from 'models'
import { EDITOR_CONTEXT_MENU_STORE } from 'store/EditorContextMenuStore'
import { EDITOR_ACTION_HISTORY_STORE } from 'store/EditorActionHistoryStore'
import { areRectsOverlapping } from 'components/editor/Monitor/VideoArea/InteractiveAreaCanvas/utils'

export const EDITOR_MONITOR_STORE = 'EDITOR_MONITOR_STORE'

const initLibraryEntityDraggingInfo: DraggingInfo = {
  clientX: null, clientY: null, entityId: null, entityClass: null, state: null
}

export class EditorMonitorStore {
  rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable stageWidth: number = 0
  @observable stageHeight: number = 0
  @observable libraryEntityDraggingInfo: DraggingInfo = initLibraryEntityDraggingInfo
  @observable showIaPictures: boolean = true

  @action.bound setStageDimensions ({ width, height }) {
    this.stageWidth = width
    this.stageHeight = height
  }

  @action.bound updateLibraryEntityDraggingInfo (
    details: DraggingInfo = initLibraryEntityDraggingInfo
  ) {
    this.libraryEntityDraggingInfo = details
  }

  @action.bound switchShowAiPictures () {
    this.showIaPictures = !this.showIaPictures
  }

  @action.bound clearEditorMonitorState () {
    this.libraryEntityDraggingInfo = initLibraryEntityDraggingInfo

    this.rootStore[EDITOR_TIMELINE_STORE].clearEditorTimelineState()
  }

  @computed get scaleX (): number {
    const scaleX = this.stageWidth / this.rootStore[EDITOR_STORE].videoWidth
    return isFinite(scaleX) ? scaleX : 1
  }

  @computed get scaleY (): number {
    const scaleY = this.stageHeight / this.rootStore[EDITOR_STORE].videoHeight
    return isFinite(scaleY) ? scaleY : 1
  }

  @computed get freeSpaceAroundVideo (): number {
    const containerAspectRatio = this.stageWidth / this.stageHeight
    const { videoHeight, videoWidth } = this.rootStore[EDITOR_STORE]
    const freeSpaceAroundVideo =
      containerAspectRatio * videoHeight - videoWidth

    return freeSpaceAroundVideo
  }

  @computed get rectanglesForCurrentFrame () {
    const {
      [EDITOR_TIMELINE_STORE]: {
        currentMonitorFrame,
        currentMonitorShotId: currentShotId
      },
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        interactiveAreas,
        getEventById
      }
    } = this.rootStore

    const rectangles: Rect[] = []

    Object.values(interactiveAreas)
      .forEach(interactiveArea => {
        if (interactiveArea.shots.includes(currentShotId)) {
          const events = interactiveArea.events
            .map(getEventById)
            .sort((a, b) => a.t - b.t)

          let nextEvent: VideoEntityEvent = null

          // inEvent for the current movement
          // could be of type 'I' or null/Anchor
          const inEvent = events.find((e, i) => {
            if (
              i < events.length - 1 &&
              e.type !== 'O' &&
              e.t <= currentMonitorFrame &&
              events[i + 1].t >= currentMonitorFrame
            ) {
              nextEvent = events[i + 1]
              return true
            }

            return false
          })

          if (!inEvent || !nextEvent) return

          const scale = ([originalX, originalY, originalWidth, originalHeight]: number[]) => {
            return [
              originalX * this.scaleX,
              originalY * this.scaleY + this.freeSpaceAroundVideo / 2,
              originalWidth * this.scaleX,
              originalHeight * this.scaleY
            ]
          }

          const [inX, inY, inWidth, inHeight] = scale(inEvent.r)
          const { t: inFrame } = inEvent
          const [nextX, nextY, nextWidth, nextHeight] = scale(nextEvent.r)
          const { t: nextFrame } = nextEvent

          const totalFramesForTheMovement = nextFrame - inFrame
          const numberOfFramesAfterInFrame = currentMonitorFrame - inFrame

          const dx = (nextX - inX) / totalFramesForTheMovement
          const x = inX + (dx * numberOfFramesAfterInFrame)

          const dy = (nextY - inY) / totalFramesForTheMovement
          const y = inY + (dy * numberOfFramesAfterInFrame)

          const dWidth = (nextWidth - inWidth) / totalFramesForTheMovement
          const width = inWidth + (dWidth * numberOfFramesAfterInFrame)

          const dHeight = (nextHeight - inHeight) / totalFramesForTheMovement
          const height = inHeight + (dHeight * numberOfFramesAfterInFrame)

          const eventAtCurrentFrame = events.find(e => e && e.t === currentMonitorFrame)

          rectangles.push({
            x,
            y,
            width,
            height,
            interactiveAreaId: interactiveArea.id,
            eventAtCurrentFrame,
            zIndex: interactiveArea.zIndex || 0
          })
        }
      })

    // Sort rectangles by index to maintain proper layering
    // When index is the same, sort by interactiveAreaId for consistent ordering
    return rectangles.sort((a, b) => {
      const indexDiff = (a.zIndex || 0) - (b.zIndex || 0)
      if (indexDiff !== 0) return indexDiff
      return a.interactiveAreaId - b.interactiveAreaId
    })
  }

  @computed get selectedCurrentRectangles () {
    return this.rectanglesForCurrentFrame
      .filter(r => this.rootStore[EDITOR_INTERACTIVE_AREAS_STORE]
        .selectedInteractiveAreaIds.includes(r.interactiveAreaId))
  }

  @observable getShotsCountForEntity = computedFn(entity => {
    let count = 0
    this.rootStore[EDITOR_INTERACTIVE_AREAS_STORE]
      .getInteractiveAreasForEntity(entity.id, getEntityClass(entity))
      .forEach(({ shots }) => { count += shots.length })
    return count
  })

  @observable getColorByIAId = computedFn(iaId => {
    const ia = this.rootStore[EDITOR_INTERACTIVE_AREAS_STORE].getInteractiveAreaById(iaId)
    if (!ia) return colors.grey

    switch (ia.entityClass) {
      case EntityClassTypes.PERSON: return colors.blue
      case EntityClassTypes.PRODUCT: return colors.orange
      default: return colors.grey
    }
  })

  @observable getEntityImageByIAId = computedFn(iaId => {
    const ia = this.rootStore[EDITOR_INTERACTIVE_AREAS_STORE].getInteractiveAreaById(iaId)
    if (!ia) return null

    const { entityId, entityClass } = ia
    if (!entityId || !entityClass) return null

    let entityObj = null

    const { getPersonById, getProductById } = this.rootStore[EDITOR_STORE]

    switch (entityClass) {
      case EntityClassTypes.PERSON:
        entityObj = getPersonById(entityId)
        break
      case EntityClassTypes.PRODUCT:
        entityObj = getProductById(entityId)
        break

      default: return null
    }

    return {
      entityClass,
      src: getMainImageSrcForObj(entityObj)
    }
  })

  descaleRectDetails = ({ x, y, width, height }) => {
    return [
      x / this.scaleX,
      (y - this.freeSpaceAroundVideo / 2) / this.scaleY,
      width / this.scaleX,
      height / this.scaleY
    ]
  }

  createIA = rectDetails => {
    const {
      [EDITOR_STORE]: { videoEntityId },
      [EDITOR_TIMELINE_STORE]: {
        currentMonitorShotId,
        currentMonitorFrame,
        currentMonitorShot
      },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { saveIA, saveIas, getInteractiveAreaById },
      [EDITOR_ACTION_HISTORY_STORE]: { saveIaAction },
      [EDITOR_CONTEXT_MENU_STORE]: { parseToApiIa }
    } = this.rootStore

    // Find all existing rects that directly overlap with the new rect
    const directOverlapIds = new Set(
      this.rectanglesForCurrentFrame
        .filter(existing => areRectsOverlapping(rectDetails, existing))
        .map(r => r.interactiveAreaId)
    )

    // Build adjacency map of overlaps between all existing rects
    const allRects = this.rectanglesForCurrentFrame
    const adjacency: Record<number, number[]> = {}
    for (let i = 0; i < allRects.length; i++) {
      for (let j = i + 1; j < allRects.length; j++) {
        if (areRectsOverlapping(allRects[i], allRects[j])) {
          const aId = allRects[i].interactiveAreaId
          const bId = allRects[j].interactiveAreaId
          if (!adjacency[aId]) adjacency[aId] = []
          if (!adjacency[bId]) adjacency[bId] = []
          adjacency[aId].push(bId)
          adjacency[bId].push(aId)
        }
      }
    }

    // BFS from direct overlaps to collect the full connected group
    const groupIds = new Set<number>()
    const queue = [...directOverlapIds]
    while (queue.length > 0) {
      const id = queue.shift()!
      if (groupIds.has(id)) continue
      groupIds.add(id)
      const neighbors = adjacency[id] || []
      for (const neighbor of neighbors) {
        if (!groupIds.has(neighbor)) queue.push(neighbor)
      }
    }

    let newZIndex = 0

    if (groupIds.size > 0) {
      const groupRects = allRects.filter(r => groupIds.has(r.interactiveAreaId))

      // Sort group by current zIndex to preserve relative order
      const sortedGroup = [...groupRects]
        .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))

      // Re-index sequentially (0, 1, 2, ...), only save those whose zIndex actually changed
      const overlappingPayload = sortedGroup
        .filter((rect, position) => (rect.zIndex || 0) !== position)
        .map(rect => {
          const position = sortedGroup.indexOf(rect)
          const ia = parseToApiIa(getInteractiveAreaById(rect.interactiveAreaId))
          const action = new IaAction(IaActionTypes.CHANGE_Z_ORDER, ia)
          saveIaAction(action)

          const updatedIa = { ...ia, zIndex: position } as InteractiveAreaAPI
          action.setAfterVersion(updatedIa, true)
          return { ia: updatedIa, action }
        })

      if (overlappingPayload.length > 0) {
        saveIas(overlappingPayload)
      }

      // New IA gets the highest zIndex (on top of the entire connected group)
      newZIndex = sortedGroup.length
    }

    const newIA: InteractiveAreaAPI = {
      id: null,
      videoEntityId,
      interactiveAreaId: null,
      name: null,
      entityId: null,
      entityClass: null,
      shots: [{ id: currentMonitorShotId } as Shot],
      events: [],
      zIndex: newZIndex
    }

    const r = this.descaleRectDetails(rectDetails)

    const inEvent: VideoEntityEvent = {
      id: null,
      eventId: null,
      interactiveAreaId: null,
      t: currentMonitorFrame,
      r,
      type: VideoEventTypes.IN
    }

    const outEvent: VideoEntityEvent = {
      id: null,
      eventId: null,
      interactiveAreaId: null,
      t: currentMonitorShot.endFrame,
      r,
      type: VideoEventTypes.OUT
    }

    newIA.events.push(inEvent)
    newIA.events.push(outEvent)

    const iaAction = new IaAction(IaActionTypes.CREATE, newIA)
    saveIaAction(iaAction)

    saveIA(newIA, iaAction)
  }

  changeRectangle = (newAttrs, interactiveAreaId: number) => {
    const {
      [EDITOR_TIMELINE_STORE]: { currentMonitorFrame },
      [EDITOR_CONTEXT_MENU_STORE]: { parseToApiIa },
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        setEvent,
        getInteractiveAreaById,
        saveIA
      },
      [EDITOR_ACTION_HISTORY_STORE]: { saveIaAction }
    } = this.rootStore

    const ia = parseToApiIa(getInteractiveAreaById(interactiveAreaId))

    const action = new IaAction(IaActionTypes.CHANGE, ia)
    saveIaAction(action)

    const event = ia.events.find(({ t }) => t === currentMonitorFrame)

    const r = this.descaleRectDetails(newAttrs)
    let newEvent: VideoEntityEvent = null

    if (event) {
      event.r = r
      setEvent(event)
    } else {
      newEvent = {
        id: null,
        interactiveAreaId,
        eventId: null,
        t: currentMonitorFrame,
        r,
        type: null
      }
    }

    newEvent && ia.events.push(newEvent)

    action.setAfterVersion(ia, true)

    saveIA(ia, action)
  }

  assignEntityToIA = (entityId, entityClass, interactiveAreaId) => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: { getInteractiveAreaById, saveIA },
      [EDITOR_CONTEXT_MENU_STORE]: { parseToApiIa },
      [EDITOR_ACTION_HISTORY_STORE]: { saveIaAction }
    } = this.rootStore
    const ia = parseToApiIa(getInteractiveAreaById(interactiveAreaId))
    if (!ia) return

    const action = new IaAction(IaActionTypes.ATTACH_ENTITY, ia)
    saveIaAction(action)

    const body: InteractiveAreaAPI = {
      ...ia,
      entityId,
      entityClass
    }
    saveIA(body, action)
  }

  deleteInteractiveArea = (id, iaAction?: IaAction) => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: { deleteIaById, getInteractiveAreaById },
      [EDITOR_CONTEXT_MENU_STORE]: { parseToApiIa },
      [EDITOR_ACTION_HISTORY_STORE]: { saveIaAction, setNoActionInProgress },
      [GENERAL_STORE]: { handleApiError }
    } = this.rootStore

    const action = iaAction || new IaAction(IaActionTypes.DELETE)
    !iaAction && saveIaAction(action)

    return Api
      .get(`/video/interactiveArea/${id}/delete`)
      .then(() => {
        const ia = parseToApiIa(getInteractiveAreaById(id))
        action.setBeforeVersion(ia)
        ia.id = null
        ia.interactiveAreaId = null
        action.setAfterVersion(ia)
        setNoActionInProgress()
        deleteIaById(id)
      })
      .catch(handleApiError)
  }

  deleteIas = idList => {
    idList.forEach(id => this.deleteInteractiveArea(id))
  }

  markAs = (eventType: VideoEventTypes, newAttrs, iaId) => {
    const {
      [EDITOR_TIMELINE_STORE]: { currentMonitorFrame },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { getInteractiveAreaById, saveIA },
      [EDITOR_CONTEXT_MENU_STORE]: { parseToApiIa },
      [EDITOR_ACTION_HISTORY_STORE]: { saveIaAction }
    } = this.rootStore

    // const beforeVersion = { ...parseToApiIa(getInteractiveAreaById(iaId)) }
    const IA = parseToApiIa(getInteractiveAreaById(iaId))
    const action = new IaAction(IaActionTypes.MARK_POINT, IA)
    saveIaAction(action)

    IA.events = IA.events
      .sort((a, b) => a.t - b.t)

    IA.shots = IA.shots
      .sort((a, b) => a.startFrame - b.startFrame)

    let eventIndex = IA.events
      .findIndex(e => e.t === currentMonitorFrame)

    if (eventIndex < 0) {
      const r = this.descaleRectDetails(newAttrs)
      const newEvent: VideoEntityEvent = {
        id: null,
        eventId: null,
        interactiveAreaId: null,
        t: currentMonitorFrame,
        r,
        type: eventType
      }

      IA.events.push(newEvent)
      IA.events = IA.events.sort((a, b) => a.t - b.t)

      eventIndex = IA.events
        .findIndex(e => e.t === currentMonitorFrame)
    } else {
      IA.events[eventIndex].type = eventType
    }

    const eventsToRemove: VideoEntityEvent[] = []

    const startIndex = {
      [VideoEventTypes.OUT]: eventIndex + 1,
      [VideoEventTypes.IN]: eventIndex - 1
    }[eventType]

    const condition = i => {
      return {
        [VideoEventTypes.OUT]: i < IA.events.length,
        [VideoEventTypes.IN]: i >= 0
      }[eventType]
    }

    for (let i = startIndex; condition(i); (eventType === VideoEventTypes.OUT ? i++ : i--)) {
      eventsToRemove.push(IA.events[i])
      if (IA.events[i].type === eventType) break
    }

    IA.events = IA.events.filter(e => !eventsToRemove.includes(e))

    action.setAfterVersion(IA, true)

    saveIA(IA, action)
  }

  descaleRectPayload = () => {
    const { scaleX, scaleY, freeSpaceAroundVideo } = this
    return {
      scaleX,
      scaleY,
      freeSpaceAroundVideo
    }
  }
}
